import React from "react";
import {
  AppleIcon,
  GooglePlayIcon,
  PhotoCameraIcon,
  VideoCameraIcon,
} from "../Common/Icons";

interface StoreBadgeProps {
  href: string;
  platform: "appstore" | "playstore";
  ariaLabel: string;
  dotVariant?: "default" | "purple";
}

const StoreBadge: React.FC<StoreBadgeProps> = ({
  href,
  platform,
  ariaLabel,
  dotVariant = "default",
}) => {
  const isAppStore = platform === "appstore";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="lr-cta__badge"
      aria-label={ariaLabel}
    >
      {isAppStore ? <AppleIcon /> : <GooglePlayIcon />}
      <div>
        <div className="lr-cta__badge-eyebrow">
          {isAppStore ? "Download on the" : "Get it on"}
        </div>
        <div className="lr-cta__badge-title">
          {isAppStore ? "App Store" : "Google Play"}
        </div>
      </div>
      <span
        className={`lr-cta__badge-dot ${dotVariant === "purple" ? "lr-cta__badge-dot--purple" : ""
          }`}
      />
    </a>
  );
};

/**
 * App Store / Google Play badges for both eZy Watermark Photos and eZy Watermark Videos apps.
 * Separate cards clearly indicate the distinct applications for photos vs videos.
 */
const CTASection: React.FC = () => {
  return (
    <section id="download" className="lr-section">
      <div className="lr-cta">
        <div className="lr-cta__card">
          <div className="lr-cta__glow" />
          <div className="lr-cta__content">
            <h2 className="lr-cta__heading">
              Your photos & videos.{" "}
              <span className="lr-text-gradient">Your mark.</span>
            </h2>
            <p className="lr-cta__lede">
              Available on iOS and Android. Choose your dedicated app below to get started.
            </p>

            <div className="lr-cta__apps">
              {/* Photos App Card */}
              <div className="lr-cta__app-card">
                <div className="lr-cta__app-header">
                  <div className="lr-cta__app-icon lr-cta__app-icon--photo">
                    <PhotoCameraIcon />
                  </div>
                  <div className="lr-cta__app-info">
                    <h3 className="lr-cta__app-title">eZy Watermark Photos</h3>
                    <p className="lr-cta__app-desc">
                      Watermark single or batch photos with text, logos, signatures & QR codes.
                    </p>
                  </div>
                </div>

                <div className="lr-cta__badges">
                  <StoreBadge
                    href="https://apps.apple.com/us/app/ezy-watermark-photos-lite/id494473910"
                    platform="appstore"
                    ariaLabel="Download eZy Watermark Photos on the App Store"
                  />
                  <StoreBadge
                    href="https://play.google.com/store/apps/details?id=com.whizpool.ezywatermarklite"
                    platform="playstore"
                    ariaLabel="Get eZy Watermark Photos on Google Play"
                  />
                </div>
              </div>

              {/* Videos App Card */}
              <div className="lr-cta__app-card">
                <div className="lr-cta__app-header">
                  <div className="lr-cta__app-icon lr-cta__app-icon--video">
                    <VideoCameraIcon />
                  </div>
                  <div className="lr-cta__app-info">
                    <h3 className="lr-cta__app-title">eZy Watermark Videos</h3>
                    <p className="lr-cta__app-desc">
                      Protect your videos, clips & reels with customizable watermarks & timestamps.
                    </p>
                  </div>
                </div>

                <div className="lr-cta__badges">
                  <StoreBadge
                    href="https://apps.apple.com/us/app/ezy-watermark-videos/id649694068"
                    platform="appstore"
                    ariaLabel="Download eZy Watermark Videos on the App Store"
                    dotVariant="purple"
                  />
                  <StoreBadge
                    href="https://play.google.com/store/apps/details?id=com.whizpool.ezyvideowatermarklite"
                    platform="playstore"
                    ariaLabel="Get eZy Watermark Videos on Google Play"
                    dotVariant="purple"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
